import React, { useEffect, useState, Children, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, FlaskConical, Microscope, ShieldCheck, ArrowRight, CheckCircle2, AlertCircle, Lock, ShoppingCart, ClipboardList, Star, Truck, Shield, Zap, Brain, Dumbbell, Moon, Heart, Leaf, DollarSign, BadgeCheck, Play, Sprout, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { shopData, Category, Product } from './shopData';
import { useCart } from '../../../contexts/CartContext';
import { usePrice } from '../../../contexts/PriceContext';
import { useBestSellers } from '../../../utils/useBestSellers';
import { useReviewStats } from '../../../utils/useReviewStats';
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xkoyvnke';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
// Stylized bottle mockup — CSS-only since real product photos don't exist yet
function BottleMockup({
  product
}: {
  product: Product;
}) {
  const colorMap = {
    charcoal: 'bg-charcoal',
    burgundy: 'bg-burgundy',
    navyslate: 'bg-navyslate',
    tealslate: 'bg-tealslate'
  };
  const accentMap = {
    tealslate: 'bg-tealslate',
    lightteal: 'bg-lightteal'
  };
  return <div className="relative w-full h-full flex items-end justify-center pb-4" data-id="element-2627">
      {/* Bottle */}
      <div className="relative w-[55%] aspect-[1/2.2] flex flex-col" data-id="element-2628">
        {/* Cap */}
        <div className={`${accentMap[product.accentColor]} h-[12%] rounded-t-md mx-2 shadow-md`} data-id="element-2629" />
        {/* Neck */}
        <div className={`${colorMap[product.bottleColor]} h-[5%] mx-3 opacity-70`} data-id="element-2630" />
        {/* Body */}
        <div className={`${colorMap[product.bottleColor]} flex-1 rounded-md shadow-2xl relative overflow-hidden border border-white/5`} data-id="element-2631">
          {/* Highlight */}
          <div className="absolute top-2 left-2 bottom-2 w-[8%] bg-white/10 rounded-full blur-sm" data-id="element-2632" />

          {/* Label */}
          <div className="absolute inset-x-3 top-[20%] bottom-[20%] bg-[#F9F9F9] flex flex-col items-center justify-between p-3 shadow-inner" data-id="element-2633">
            {/* Top: brand */}
            <div className="text-center" data-id="element-2634">
              <p className="text-[6px] md:text-[7px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em]" data-id="element-2635">
                Effective Dose
              </p>
              <div className={`w-3 h-[1px] ${accentMap[product.accentColor]} mx-auto mt-1`} data-id="element-2636" />
            </div>

            {/* Middle: product name */}
            <div className="text-center" data-id="element-2637">
              <p className="text-[8px] md:text-[10px] font-serif text-[#1F1F1F] leading-tight" data-id="element-2638">
                {product.name}
              </p>
            </div>

            {/* Bottom: dose */}
            <div className="text-center" data-id="element-2639">
              <p className="text-[14px] md:text-[18px] font-serif font-bold text-[#1F1F1F] leading-none" data-id="element-2640">
                {product.dose}
              </p>
              <p className="text-[5px] md:text-[6px] text-[#7A7A70] uppercase tracking-widest mt-0.5" data-id="element-2641">
                {product.doseUnit}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Floor shadow */}
      <div className="absolute bottom-0 left-1/4 right-1/4 h-2 bg-black/30 rounded-full blur-md" data-id="element-2642" />
    </div>;
}
interface WaitlistFormProps {
  productName?: string;
}
function WaitlistForm({
  productName
}: WaitlistFormProps) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    if (formData.get('_gotcha')) {
      setStatus('success');
      return;
    }
    setStatus('submitting');
    setError('');
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data?.error || 'Something went wrong.');
        setStatus('error');
      }
    } catch {
      setError('Network error. Try again.');
      setStatus('error');
    }
  };
  if (status === 'success') {
    return <div className="bg-tealslate/20 border-l-4 border-tealslate p-6 flex gap-4 items-start" data-id="element-2643">
        <CheckCircle2 className="w-6 h-6 text-tealslate shrink-0" data-id="element-2644" />
        <div data-id="element-2645">
          <p className="font-bold text-white mb-1" data-id="element-2646">You're on the list.</p>
          <p className="text-sm text-[#D8D8D0]" data-id="element-2647">
            We'll email you the moment Effective Dose launches.
          </p>
        </div>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="w-full" data-id="element-2648">
      <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" style={{
      position: 'absolute',
      left: '-9999px'
    }} aria-hidden="true" data-id="element-2649" />
      <input type="hidden" name="form_type" value="effective_dose_waitlist" data-id="element-2650" />
      {productName && <input type="hidden" name="product_interest" value={productName} data-id="element-2651" />}

      <div className="flex flex-col sm:flex-row gap-3" data-id="element-2652">
        <input type="email" name="email" placeholder="Enter your email" required className="flex-1 bg-bodybg border border-darkgray px-5 py-4 text-white placeholder:text-[#7A7A70] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2653" />
        <button type="submit" disabled={status === 'submitting'} className="px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-lightteal transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-2654">
          {status === 'submitting' ? <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2655" />
              Joining…
            </> : <>
              Notify Me <ArrowRight className="w-4 h-4" data-id="element-2656" />
            </>}
        </button>
      </div>

      {status === 'error' && <div className="mt-3 flex items-center gap-2 text-sm text-burgundy" data-id="element-2657">
          <AlertCircle className="w-4 h-4" data-id="element-2658" />
          {error}
        </div>}
    </form>;
}
export function Shop() {
  const [activeFilter, setActiveFilter] = useState<'All' | Category | 'Bestsellers'>('All');
  const [selectedCategories, setSelectedCategories] = useState<Set<Category>>(new Set());
  const {
    addItem
  } = useCart();
  // Best sellers are derived automatically from real sales data — empty on a
  // brand-new site, then filled in as products start outselling each other.
  const bestSellers = useBestSellers();
  const reviewStats = useReviewStats();
  // Compute category counts
  const categoryCounts = shopData.reduce((acc, p) => {
    acc[p.category] = (acc[p.category] || 0) + 1;
    return acc;
  }, {} as Record<Category, number>);
  // Filter logic
  let filtered = shopData;
  if (activeFilter === 'Bestsellers') {
    filtered = shopData.filter(p => bestSellers.has(p.id));
  } else if (activeFilter !== 'All') {
    filtered = shopData.filter(p => p.category === activeFilter);
  }
  // Apply sidebar checkboxes (multi-select)
  if (selectedCategories.size > 0) {
    filtered = filtered.filter(p => selectedCategories.has(p.category));
  }
  const toggleCategory = (cat: Category) => {
    const next = new Set(selectedCategories);
    if (next.has(cat)) next.delete(cat);else next.add(cat);
    setSelectedCategories(next);
  };
  return <div className="w-full min-h-screen bg-white" data-id="element-2659">
      {/* HERO CAROUSEL */}
      <HeroCarousel data-id="element-2660" />

      <section className="py-12 md:py-16 px-6" data-id="element-2661">
        <div className="max-w-7xl mx-auto" data-id="element-2662">
          {/* Breadcrumb */}
          <div className="text-sm text-[#5A5A5A] mb-6" data-id="element-2663">
            <Link to="/" className="hover:text-tealslate transition-colors underline decoration-[#E5E5E5] underline-offset-4" data-id="element-2664">
              Home
            </Link>{' '}
            <span className="mx-2" data-id="element-2665">/</span> Shop All Supplements
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-[#1F1F1F] mb-4 tracking-tight" data-id="element-2666">
            Shop All Supplements
          </h1>
          <p className="text-base text-[#5A5A5A] mb-10" data-id="element-2667">
            Explore our full range of clean, science-backed supplements.
          </p>

          {/* Category Pills */}
          <div className="flex flex-wrap gap-3 mb-12" data-id="element-2668">
            {['All', 'Bestsellers', 'Protein', 'Strength', 'Endurance', 'Health', 'Stress', 'Sleep'].map(f => <button key={f} onClick={() => setActiveFilter(f as any)} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${activeFilter === f ? 'bg-[#1F1F1F] text-white border border-[#1F1F1F]' : 'bg-white border border-[#E5E5E5] text-[#5A5A5A] hover:border-[#1F1F1F] hover:text-[#1F1F1F]'}`} data-id="element-2669">
                {f === 'All' ? 'All Products' : f}
              </button>)}
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-10" data-id="element-2670">
            {/* LEFT Sidebar */}
            <aside className="lg:col-span-1" data-id="element-2671">
              <h3 className="text-2xl font-bold text-[#1F1F1F] mb-6" data-id="element-2672">
                Filter products
              </h3>
              <details open className="group border-t border-[#E5E5E5] pt-5" data-id="element-2673">
                <summary className="cursor-pointer list-none flex items-center justify-between text-base font-bold text-[#1F1F1F] mb-4" data-id="element-2674">
                  Category
                  <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" data-id="element-2675" />
                </summary>
                <div className="space-y-3 pb-5" data-id="element-2676">
                  {(['Protein', 'Strength', 'Endurance', 'Health', 'Stress', 'Sleep'] as Category[]).map(cat => <label key={cat} className="flex items-center justify-between cursor-pointer text-sm text-[#5A5A5A] hover:text-[#1F1F1F] transition-colors" data-id="element-2677">
                      <div className="flex items-center gap-3" data-id="element-2678">
                        <input type="checkbox" checked={selectedCategories.has(cat)} onChange={() => toggleCategory(cat)} className="w-4 h-4 rounded border-[#E5E5E5] text-[#1F1F1F] focus:ring-[#1F1F1F]" data-id="element-2679" />
                        {cat}
                      </div>
                      <span className="text-xs text-[#9A9A90]" data-id="element-2680">
                        ({categoryCounts[cat] || 0})
                      </span>
                    </label>)}
                </div>
              </details>
            </aside>

            {/* RIGHT Product Grid */}
            <div className="lg:col-span-3" data-id="element-2681">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2682">
                {filtered.map(product => <ShopProductCard key={product.id} product={product} addItem={addItem} isBestseller={bestSellers.has(product.id)} reviewStats={reviewStats[product.id]} data-id="element-2683" />)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VERIFIED REVIEWS CAROUSEL */}
      <ReviewsCarousel data-id="element-2684" />
    </div>;
}
function ShopProductCard({
  product,
  addItem,
  isBestseller,
  reviewStats
}: {
  product: Product;
  addItem: ReturnType<typeof useCart>['addItem'];
  isBestseller: boolean;
  reviewStats?: {
    avg: number;
    count: number;
  };
}) {
  const {
    cents
  } = usePrice(product.lookupKey, product.price);
  const handleAdd = () => {
    if (cents === null || !product.lookupKey) return;
    addItem({
      productId: product.id,
      name: product.name,
      unitPrice: cents,
      type: 'supplement',
      lookupKey: product.lookupKey
    });
  };
  return <div className="bg-white border border-[#E5E5E5] overflow-hidden hover:shadow-lg transition-shadow flex flex-col h-full" data-id="element-2685">
      <Link to={`/shop/${product.id}`} className="relative aspect-square bg-white flex items-center justify-center p-6 group-hover:opacity-90 transition-opacity block" data-id="element-2686">
        {isBestseller && <span className="absolute top-3 left-3 z-10 px-2.5 py-1 bg-[#1F73B7] text-white text-[10px] font-bold tracking-wide rounded-sm" data-id="element-2687">
            Best Seller
          </span>}
        <img src={product.image} alt={product.name} className="w-full h-full object-contain" data-id="element-2688" />
      </Link>
      <div className="p-5 flex flex-col flex-1" data-id="element-2689">
        {/* Star rating */}
        <div className="flex items-center gap-0.5 mb-3" data-id="element-2690">
          {[1, 2, 3, 4, 5].map(star => <Star key={star} className={`w-3.5 h-3.5 ${star <= Math.round(reviewStats?.avg || 0) ? 'fill-[#1F73B7] text-[#1F73B7]' : 'text-[#D8D8D0]'}`} data-id="element-2691" />)}
          <span className="text-xs text-[#9A9A90] ml-1.5" data-id="element-2692">
            {reviewStats?.count || 0} Reviews
          </span>
        </div>
        <Link to={`/shop/${product.id}`} className="hover:underline decoration-[#1F1F1F] underline-offset-2" data-id="element-2693">
          <h3 className="text-lg font-bold text-[#1F1F1F] mb-2 leading-tight" data-id="element-2694">
            {product.name}
          </h3>
        </Link>
        <p className="text-sm text-[#5A5A5A] mb-5 line-clamp-2 flex-1" data-id="element-2695">
          {product.shortDescription}
        </p>

        <div className="flex items-center justify-between mt-auto pt-4 border-t border-[#E5E5E5]" data-id="element-2696">
          <p className="text-lg font-bold text-[#1F1F1F]" data-id="element-2697">
            {cents !== null ? `${(cents / 100).toFixed(2)}` : '—'}
          </p>
          <button onClick={handleAdd} disabled={cents === null || !product.lookupKey} className="px-5 py-2.5 bg-[#1F1F1F] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#3B3B3B] transition-colors disabled:opacity-40 rounded" data-id="element-2698">
            Add to Cart
          </button>
        </div>
      </div>
    </div>;
}
const HERO_SLIDES = [{
  bg: 'https://cdn.magicpatterns.com/uploads/jZM2tEohB8P3RXVY53wDZo/file_0000000050e471fd9b72564486af2c12.png',
  eyebrow: 'Science-backed. Dietitian approved.',
  headlineStart: 'Supplements that support your',
  headlineEnd: 'best life.',
  subhead: 'High-quality, evidence-based supplements to help you feel better, every day.',
  primaryCta: {
    label: 'Shop All Supplements',
    href: '#products'
  },
  secondaryCta: {
    label: 'Our Promise',
    href: '#products'
  }
}, {
  bg: 'https://cdn.magicpatterns.com/uploads/f87EatSxUMfcpQZBHWscm9/file_00000000dce071fd9bbf2806563c1bcd.png',
  eyebrow: 'No fairy dust. No fillers.',
  headlineStart: 'Effective doses.',
  headlineEnd: 'Always.',
  subhead: 'Every product is dosed at the level research demands, not the level that protects margins.',
  primaryCta: {
    label: 'Browse Products',
    href: '#products'
  },
  secondaryCta: {
    label: 'Why It Matters',
    href: '#products'
  }
}, {
  bg: 'https://cdn.magicpatterns.com/uploads/jZM2tEohB8P3RXVY53wDZo/file_0000000050e471fd9b72564486af2c12.png',
  eyebrow: 'No proprietary blends. No junk.',
  headlineStart: 'Clinical evidence,',
  headlineEnd: 'in your routine.',
  subhead: 'Formulated by a Registered Dietitian who actually reads the research. Designed for your real-world results.',
  primaryCta: {
    label: 'Browse Products',
    href: '#products'
  },
  secondaryCta: {
    label: 'Shop the Line',
    href: '#products'
  }
}];
function HeroCarousel() {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setIndex(i => (i + 1) % HERO_SLIDES.length);
    }, 6500);
    return () => clearInterval(t);
  }, []);
  const slide = HERO_SLIDES[index];
  const heroBottles = [shopData.find(p => p.id === 'multivitamin-mens'), shopData.find(p => p.id === 'omega-3'), shopData.find(p => p.id === 'creatine')].filter((p): p is Product => Boolean(p));
  const next = () => setIndex(i => (i + 1) % HERO_SLIDES.length);
  const prev = () => setIndex(i => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  return <section className="relative pt-28 pb-12 md:pb-16 px-6 overflow-hidden" data-id="element-2699">
      {/* Animated background image */}
      <AnimatePresence mode="wait" data-id="element-2700">
        <motion.div key={`bg-${index}`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.8
      }} className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `url('${slide.bg}')`
      }} data-id="element-2701" />
      </AnimatePresence>

      {/* Dark gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/40 pointer-events-none" data-id="element-2702" />

      <div className="relative max-w-7xl mx-auto" data-id="element-2703">
        <div className="min-h-[520px] flex items-center" data-id="element-2704">
          {/* Carousel Text */}
          <div className="relative w-full" data-id="element-2705">
            <AnimatePresence mode="wait" data-id="element-2706">
              <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.5
            }} data-id="element-2707">
                <p className="text-[10px] md:text-xs font-bold text-cream/90 uppercase tracking-[0.25em] mb-6" data-id="element-2708">
                  {slide.eyebrow}
                </p>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white leading-[1.1] mb-2" data-id="element-2709">
                  {slide.headlineStart}
                </h1>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-cream italic leading-[1.1] mb-6" data-id="element-2710">
                  {slide.headlineEnd}
                </h1>
                <p className="text-base md:text-lg text-white/80 mb-8 max-w-md leading-relaxed" data-id="element-2711">
                  {slide.subhead}
                </p>
                <div className="flex flex-col sm:flex-row items-start gap-4 mb-10" data-id="element-2712">
                  <a href={slide.primaryCta.href} className="px-7 py-3.5 bg-tealslate text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#4A5A5F] transition-colors" data-id="element-2713">
                    {slide.primaryCta.label}
                  </a>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Trust badges row (static, shared across slides) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-lg" data-id="element-2714">
              {[{
              icon: Leaf,
              label: 'Dietitian Formulated'
            }, {
              icon: FlaskConical,
              label: 'Premium Ingredients'
            }, {
              icon: Sprout,
              label: 'No Artificial Fillers'
            }].map((b, i) => <div key={i} className="flex flex-col items-start text-left" data-id="element-2715">
                  <b.icon className="w-5 h-5 text-cream mb-2" data-id="element-2716" />
                  <span className="text-[11px] text-white/85 leading-tight" data-id="element-2717">
                    {b.label}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}
// ─── Verified reviews carousel ───────────────────────────────────────────
function ReviewsCarousel() {
  const reviewStats = useReviewStats();
  const totalReviews = Object.values(reviewStats).reduce((sum, stat) => sum + stat.count, 0);
  // Overall average across every product's reviews (weighted by count).
  const weightedSum = Object.values(reviewStats).reduce((sum, stat) => sum + stat.avg * stat.count, 0);
  const overallAvg = totalReviews > 0 ? weightedSum / totalReviews : 0;
  const filledStars = Math.round(overallAvg);
  if (totalReviews === 0) {
    return <section className="bg-[#F9F9F9] py-16 md:py-24 px-6 border-t border-[#E5E5E5]" data-id="element-2718">
        <div className="max-w-3xl mx-auto text-center" data-id="element-2719">
          <div className="flex items-center justify-center gap-1 mb-6" data-id="element-2720">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 text-[#D8D8D0]" data-id="element-2721" />)}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1F1F1F] leading-tight tracking-tight mb-4" data-id="element-2722">
            No reviews yet
          </h2>
          <p className="text-base text-[#5A5A5A] leading-relaxed" data-id="element-2723">
            We just launched. Be the first to try our supplements and share your
            experience — your review could be the first one here.
          </p>
        </div>
      </section>;
  }
  return <section className="bg-[#F9F9F9] py-16 md:py-24 px-6 border-t border-[#E5E5E5]" data-id="element-2724">
      <div className="max-w-3xl mx-auto text-center" data-id="element-2725">
        <div className="flex items-center justify-center gap-1 mb-6" data-id="element-2726">
          {[...Array(5)].map((_, i) => <Star key={i} className={`w-6 h-6 ${i < filledStars ? 'fill-[#1F73B7] text-[#1F73B7]' : 'text-[#D8D8D0]'}`} data-id="element-2727" />)}
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-[#1F1F1F] leading-tight tracking-tight mb-4" data-id="element-2728">
          {totalReviews.toLocaleString()} Verified{' '}
          {totalReviews === 1 ? 'Review' : 'Reviews'} From Our Customers
        </h2>
        <p className="text-base text-[#5A5A5A] leading-relaxed" data-id="element-2729">
          {overallAvg.toFixed(1)} average rating. Every review comes from a
          verified buyer — click any product to read what customers are saying.
        </p>
      </div>
    </section>;
}
interface ProductPricingProps {
  product: Product;
  addItem: ReturnType<typeof useCart>['addItem'];
}
function ProductPricing({
  product,
  addItem
}: ProductPricingProps) {
  const {
    cents,
    isLive,
    loading
  } = usePrice(product.lookupKey, product.price);
  const canBuy = cents !== null && Boolean(product.lookupKey);
  const handleAdd = () => {
    if (cents === null || !product.lookupKey) return;
    addItem({
      productId: product.id,
      name: product.name,
      unitPrice: cents,
      type: 'supplement',
      lookupKey: product.lookupKey
    });
  };
  return <div className="flex items-center justify-between gap-4 pt-5 mt-auto border-t border-[#E5E5E5]" data-id="element-2730">
      <div data-id="element-2731">
        <p className="text-[10px] font-bold text-[#5A5A5A] uppercase tracking-widest mb-0.5" data-id="element-2732">
          Price
        </p>
        {cents === null ? <div className="h-7 w-20 bg-[#E5E5E5] animate-pulse rounded-sm" data-id="element-2733" /> : <p className="text-2xl font-serif text-[#1F1F1F] leading-none" data-id="element-2734">
            ${(cents / 100).toFixed(2)}
            {!isLive && !loading && <span className="text-[10px] text-[#7A7A70] font-sans ml-2 normal-case tracking-normal" data-id="element-2735">
                · est.
              </span>}
          </p>}
      </div>
      <button onClick={handleAdd} disabled={!canBuy} className="px-5 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-lightteal transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2" title={!canBuy ? 'Not yet available for purchase' : undefined} data-id="element-2736">
        <ShoppingCart className="w-3.5 h-3.5" data-id="element-2737" />
        Add to Cart
      </button>
    </div>;
}